import { Box, Flex, Grid } from '@radix-ui/themes'
import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import {
	useRouteLoaderData,
	useSearchParams,
	useOutletContext,
	useFetcher,
	type ShouldRevalidateFunctionArgs,
} from '@remix-run/react'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Pager } from '~/components/common/pager'
import { type GetAddress } from '~/models/ecommerce-providers/sylius/shop/fetch/address/get-address'
import { type GetAddresses } from '~/models/ecommerce-providers/sylius/shop/fetch/address/get-addresses'
import { type Locale } from '~/models/locale'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import { type loader as addressesLoader } from '~/routes/($lang).profile.addresses/addresses.server'
import { AddressItem } from '~/routes/($lang).profile.addresses._index/address-item'
import { Header } from '~/routes/($lang).profile.addresses._index/header'
import { type loader } from '~/routes/($lang).profile.addresses._index/index.server'
import { NoItems } from '~/routes/($lang).profile.addresses._index/no-items'
import { PROFILE_ADDRESSES_CREATE_FORM } from '~/routes/($lang).profile.addresses.create/create.component'
import { type Context, ROOT_INPUTS } from '~/routes/_layout/root.component'
import { type loader as routeLoader } from '~/routes/_layout/root.server'
import { defaultMetaI18next } from '~/utils/client/default-meta-i18next.ts'
import { useOptimisticAddresses } from '~/utils/hooks/use-optimistic-addresses.ts'
import { usePagerViewCreated } from '~/utils/hooks/use-pager-view-created.ts'

type ComponentProps = {
	addresses?: GetAddresses
}

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeProfileAddressesIndex = 'route-profile-addresses-index',
	uiPager = 'ui-pager',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang).profile.addresses._index',
	profile = 'routes/($lang).profile',
	profileAddresses = 'routes/($lang).profile.addresses',
}

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<
	typeof loader,
	{
		[ROUTE_LOADER.root]: typeof routeLoader
		[ROUTE_LOADER.profileAddresses]: typeof addressesLoader
	}
> = ({ data, matches }: MetaArgs) => {
	const parentMeta = matches.reduce((a, b) =>
		a.meta.length > b.meta.length ? a : b,
	).meta
	const title = parentMeta.find((meta) => 'title' in meta)
	const description = parentMeta.find(
		(meta) => 'name' in meta && meta.name === 'description',
	)

	const rootData = matches.find((match) => match.id === ROUTE_LOADER.root)!
		.data as { locale: Locale }

	const profileAddressesData = matches.find(
		(match) => match.id === ROUTE_LOADER.profileAddresses,
	)!.data as { addresses: GetAddresses }

	// @ts-ignore
	return uniqWith(
		title?.title.includes('Error')
			? [...parentMeta]
			: data
				? [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `${data.meta.title} | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `${data.meta.description} | ${description?.content}`,
						},
						// Structured data -> ld+json
						{
							'script:ld+json': profileAddressesData.addresses.hydra,
						},
						// I18next
						...defaultMetaI18next<typeof I18NEXT>({
							I18NEXT,
							locale: rootData.locale,
						}),
					]
				: [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `Error | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `Error | ${description?.content}`,
						},
					],
		(a, b) => isEqual(a, b),
	)
}

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC<ComponentProps> = ({ addresses }) => {
	const addressesLoaderData = useRouteLoaderData<typeof addressesLoader>(
		ROUTE_LOADER.profileAddresses,
	)!
	const { customer } = useRouteLoaderData<typeof routeLoader>(
		ROUTE_LOADER.profile,
	)
	const { locale } = useOutletContext<Context>()
	const pagerViewCreated = usePagerViewCreated(
		addressesLoaderData.addresses.hydra!,
		'3',
	)
	const [searchParams] = useSearchParams()
	const { t } = useTranslation(I18NEXT.routeProfileAddressesIndex)
	const createAddressFetcher = useFetcher({
		key: PROFILE_ADDRESSES_CREATE_FORM.subaction,
	})

	const optimisticAddresses = useOptimisticAddresses({
		addresses,
		addressesLoaderData: addressesLoaderData.addresses,
	})

	return optimisticAddresses.hydra!['hydra:member']!.length ||
		createAddressFetcher.formData ? (
		<Flex
			direction="column"
			justify="center"
			width="100%"
			gapY="6"
			ml={{ lg: '7' }}
		>
			<Header t={t} locale={locale} pagerViewCreated={pagerViewCreated} />
			{/*
      <Notifies
        t={t}
        searchParams={searchParams}
      />
      */}
			<Grid columns={{ initial: '1', xl: '3' }} gap="5" justify="center">
				{optimisticAddresses.items.map(
					({
						firstName,
						lastName,
						countryCode,
						street,
						city,
						postcode,
						phoneNumber,
						company,
						'@id': iri,
						id,
					}: GetAddress) => (
						<AddressItem
							key={id}
							customer={customer}
							iri={iri!}
							firstName={firstName!}
							lastName={lastName!}
							countryCode={countryCode!}
							street={street!}
							city={city!}
							postcode={postcode!}
							phoneNumber={phoneNumber!}
							company={company!}
							id={id!}
						/>
					),
				)}
			</Grid>
			<Box asChild mt="8">
				<Pager
					hydra={optimisticAddresses.hydra!['hydra:view']}
					preventScrollReset
				/>
			</Box>
		</Flex>
	) : (
		<NoItems
			t={t}
			pagerViewCreated={pagerViewCreated}
			addresses={optimisticAddresses}
			searchParams={searchParams}
		/>
	)
}
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
	Component,
	shouldRevalidate,
	handle,
	meta,
	I18NEXT as PROFILE_ADDRESSES_INDEX_I18NEXT,
	ROUTE_LOADER as PROFILE_ADDRESSES_INDEX_ROUTE_LOADER,
}
